// Transport Cost Module (Local Transport & Freight Register) — Admin Panel & Dispatch
// Details updated in Point No. 55 (Handoff Freight Terms: Paid, To Pay, Self, Add new)
// and Point No. 56 (Local Transport: Prashant Bhate, Suresh Solanki, Paid Porter, To Pay Porter, Self, Add New)
// are tracked and managed here after dispatch.
// Data: GET /api/sales-ops/orders/local-transport-log;
// Corrections: PUT /api/sales-ops/orders/:id/local-transport.

import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-hot-toast';
import { Download, Pencil, Truck } from 'lucide-react';
import { api, getApiErrorMessage } from '@/shared/api/axios';
import { useUser } from '@/shared/hooks/useAuth';
import { Button } from '@/shared/components/ui/Button';
import { PageHeader } from '@/shared/components/ui/PageHeader';
import { Skeleton } from '@/shared/components/ui/Skeleton';
import { canMarkDispatch, canSetTransportAmount } from '@/shared/utils/dispatchAccess';
import { formatINR } from '@/shared/utils/currency';
import { LocalTransportModal, localTransportToForm, announceVehicleShare, type LocalTransport } from '../components/LocalTransportModal';

interface Row {
  _id: string;
  orderNumber: string;
  customerName: string;
  destination: string;
  orderValue: number;
  dispatchedAt: string | null;
  status: string;
  transporter: string;
  courierName?: string;
  localTransport: Record<string, unknown>;
  /** true = local transport used, false = the third party picked up directly, null = not said. */
  localTransportUsed?: boolean | null;
  transportMode?: string;
  /** Third-party freight, entered by Admin / Superadmin. */
  freight?: { amount?: number | null; payment?: string } | null;
  handoffFreightTerms?: string;
  thirdPartyTransport?: Record<string, unknown> | null;
  handoffNote?: string;
  requiredDispatchDate?: string | null;
  /** Export order — third-party freight applies only to these (2026-10-06). */
  isExport?: boolean;
  handedOffAt?: string | null;
}

const dayKey = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
const PRESETS: { key: string; label: string; range: () => [string, string] }[] = [
  { key: 'today', label: 'Today', range: () => { const t = dayKey(new Date()); return [t, t]; } },
  { key: 'week', label: 'This week', range: () => { const n = new Date(); const s = new Date(n); s.setDate(n.getDate() - ((n.getDay() + 6) % 7)); return [dayKey(s), dayKey(n)]; } },
  { key: 'month', label: 'This month', range: () => { const n = new Date(); return [dayKey(new Date(n.getFullYear(), n.getMonth(), 1)), dayKey(n)]; } },
  { key: 'last_month', label: 'Last month', range: () => { const n = new Date(); return [dayKey(new Date(n.getFullYear(), n.getMonth() - 1, 1)), dayKey(new Date(n.getFullYear(), n.getMonth(), 0))]; } },
  { key: 'all', label: 'All', range: () => ['', ''] },
];

const s = (v: unknown) => (v === null || v === undefined ? '' : String(v));

const amountOf = (r: Row) => (r.localTransportUsed === false ? 0 : Number(r.localTransport?.amount) || 0);
const hasFreight = (r: Row) => !!(r.transporter || r.courierName || r.transportMode === 'third_party_courier');
const freightOf = (r: Row) => (hasFreight(r) ? Number(r.freight?.amount) || 0 : 0);

const payLabel = (p: unknown) => {
  const str = String(p || '').toLowerCase().trim();
  if (!str) return '';
  if (str === 'to_pay') return 'To Pay';
  if (str === 'paid') return 'Paid';
  if (str === 'self') return 'Self';
  return str.split(/[\s_-]+/).map((w) => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
};

const payBadgeClass = (p: unknown) => {
  const str = String(p || '').toLowerCase().trim();
  if (str === 'paid') return 'bg-emerald-50 text-emerald-700 border-emerald-200';
  if (str === 'to_pay') return 'bg-amber-50 text-amber-700 border-amber-200';
  if (str === 'self') return 'bg-blue-50 text-blue-700 border-blue-200';
  return 'bg-purple-50 text-purple-700 border-purple-200';
};

// Two different decisions, kept apart (client, 2026-10-23): the HAND-OFF terms
// are what Sales chose for the third-party transporter (ACPL, VRL…); the LOCAL
// terms are what Dispatch set for the leg to that transporter (Porter, tempo…).
// The hand-off term never falls back to the local one — that mixing is what
// made one "Freight Terms" filter match the wrong orders.
const handoffTransporterOf = (r: Row) => s(r.transporter || r.courierName);
const handoffTermOf = (r: Row) => String(r.handoffFreightTerms || r.freight?.payment || '').toLowerCase().trim();
const localTermOf = (r: Row) => String(r.localTransport?.payment || '').toLowerCase().trim();
const NO_TERM = '__none';
const termMatches = (filter: string, term: string) => !filter || (filter === NO_TERM ? term === '' : term === filter);
const TERM_OPTIONS = [
  { value: 'paid', label: 'Paid' },
  { value: 'to_pay', label: 'To Pay' },
  { value: 'self', label: 'Self' },
  { value: NO_TERM, label: 'Not set' },
];

const usedLabel = (r: Row) => (r.localTransportUsed === false ? 'No' : r.localTransportUsed === true || s(r.localTransport?.name) ? 'Yes' : '—');

export function LocalTransportRegisterPage() {
  const qc = useQueryClient();
  const user = useUser();
  const canEdit = canMarkDispatch(user) || canSetTransportAmount(user) || String(user?.department || '').toLowerCase() === 'sales';
  const [preset, setPreset] = useState('month');
  const [[from, to], setRange] = useState<[string, string]>(() => PRESETS[2].range());
  const [who, setWho] = useState('');
  const [handoffWho, setHandoffWho] = useState('');
  const [handoffTermFilter, setHandoffTermFilter] = useState('');
  const [localTermFilter, setLocalTermFilter] = useState('');
  const [search, setSearch] = useState('');
  const [editing, setEditing] = useState<Row | null>(null);

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['dispatch', 'local-transport-log', from, to],
    queryFn: async () => (await api.get<{ rows: Row[] }>('/api/sales-ops/orders/local-transport-log', { params: { from: from || undefined, to: to || undefined } })).data.rows,
  });
  const rows = data ?? [];

  const transporters = useMemo(() => [...new Set(rows.map((r) => s(r.localTransport?.name)).filter(Boolean))].sort(), [rows]);
  const handoffTransporters = useMemo(() => [...new Set(rows.map(handoffTransporterOf).filter(Boolean))].sort(), [rows]);
  const anyFilter = Boolean(who || handoffWho || handoffTermFilter || localTermFilter || search);
  const clearFilters = () => { setWho(''); setHandoffWho(''); setHandoffTermFilter(''); setLocalTermFilter(''); setSearch(''); };

  const view = useMemo(() => {
    const q = search.trim().toLowerCase();
    return rows.filter((r) => {
      if (handoffWho && handoffTransporterOf(r) !== handoffWho) return false;
      if (!termMatches(handoffTermFilter, handoffTermOf(r))) return false;
      if (who && s(r.localTransport?.name) !== who) return false;
      if (!termMatches(localTermFilter, localTermOf(r))) return false;
      if (!q) return true;
      const searchable = [
        r.orderNumber,
        r.customerName,
        r.destination,
        r.transporter,
        r.courierName,
        r.handoffFreightTerms,
        r.handoffNote,
        ...Object.values(r.localTransport || {}).map(s),
      ].join(' ').toLowerCase();
      return searchable.includes(q);
    });
  }, [rows, who, handoffWho, handoffTermFilter, localTermFilter, search]);

  const total = view.reduce((n, r) => n + amountOf(r), 0);
  const freightTotal = view.reduce((n, r) => n + freightOf(r), 0);
  // Third-party freight is OPTIONAL (client, 2026-10-23: no freight cost is
  // charged at present), so only a missing local-transport amount is pending.
  const amountsPending = view.filter((r) => r.localTransportUsed !== false && s(r.localTransport?.name) && s(r.localTransport?.amount) === '').length;

  const byTransporter = useMemo(() => {
    const m = new Map<string, { trips: number; amount: number }>();
    for (const r of view) {
      const k = s(r.localTransport?.name) || (r.localTransportUsed === false ? 'Direct / No Local' : '—');
      const v = m.get(k) || { trips: 0, amount: 0 };
      v.trips += 1;
      v.amount += amountOf(r);
      m.set(k, v);
    }
    return [...m.entries()].sort((a, b) => b[1].amount - a[1].amount);
  }, [view]);

  // Hand-off terms count the orders (and any third-party freight); local terms
  // count the local legs and add up their cost.
  const byHandoffTerm = useMemo(() => {
    const m = new Map<string, { count: number; amount: number }>();
    for (const r of view) {
      const term = payLabel(handoffTermOf(r)) || 'Not set';
      const cur = m.get(term) || { count: 0, amount: 0 };
      cur.count += 1;
      cur.amount += freightOf(r);
      m.set(term, cur);
    }
    return [...m.entries()].sort((a, b) => b[1].count - a[1].count);
  }, [view]);
  const byLocalTerm = useMemo(() => {
    const m = new Map<string, { count: number; amount: number }>();
    for (const r of view) {
      if (r.localTransportUsed === false) continue;
      const term = payLabel(localTermOf(r)) || 'Not set';
      const cur = m.get(term) || { count: 0, amount: 0 };
      cur.count += 1;
      cur.amount += amountOf(r);
      m.set(term, cur);
    }
    return [...m.entries()].sort((a, b) => b[1].count - a[1].count);
  }, [view]);

  const save = useMutation({
    mutationFn: async ({ id, lt }: { id: string; lt: LocalTransport }) => (await api.put(`/api/sales-ops/orders/${id}/local-transport`, lt)).data,
    onSuccess: (data) => { setEditing(null); toast.success('Transport details saved.'); announceVehicleShare(data); qc.invalidateQueries({ queryKey: ['dispatch'] }); },
    onError: (err) => toast.error(getApiErrorMessage(err, 'Could not save the transport details.')),
  });

  const exportCsv = () => {
    const head = [
      'Dispatched on',
      'Order',
      'Client',
      'Destination',
      'Transporter / Courier',
      'Handoff Freight Terms',
      'Freight Amount',
      'Local transport used',
      'Local transporter',
      'Mobile',
      'Vehicle',
      'Local amount',
      'Local Payment Term',
      'Payment mode',
      'Tracking / LR',
      'Handoff Note',
      'Local Transport Note',
    ];
    const lines = view.map((r) => [
      r.dispatchedAt ? new Date(r.dispatchedAt).toLocaleDateString('en-IN') : 'Waiting',
      r.orderNumber,
      r.customerName,
      r.destination,
      r.transporter || r.courierName || (r.transportMode === 'local_transport' ? 'Local Transport' : 'Direct'),
      payLabel(handoffTermOf(r)),
      s(r.freight?.amount),
      usedLabel(r),
      s(r.localTransport?.name),
      s(r.localTransport?.mobile ?? r.localTransport?.driverPhone),
      s(r.localTransport?.vehicleNo),
      s(r.localTransport?.amount),
      payLabel(r.localTransport?.payment),
      s(r.localTransport?.paymentMode),
      s(r.localTransport?.trackingNo),
      s(r.handoffNote),
      s(r.localTransport?.note),
    ]);
    const csv = [head, ...lines].map((l) => l.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(',')).join('\n');
    const a = document.createElement('a');
    a.href = URL.createObjectURL(new Blob([csv], { type: 'text/csv' }));
    a.download = `transport-costs-${from || 'all'}-${to || 'all'}.csv`;
    a.click();
  };

  return (
    <div className="mx-auto max-w-[1600px] space-y-4 pb-14 text-on-surface">
      {editing && (
        <LocalTransportModal
          mode="edit"
          orderNumber={editing.orderNumber}
          sentVia={editing.transporter || editing.courierName || undefined}
          thirdParty={hasFreight(editing)}
          twoLegs={editing.transportMode === 'third_party_courier'}
          localOnly={editing.transportMode === 'local_transport' || editing.localTransportUsed === true}
          initial={localTransportToForm(editing.localTransport, {
            localTransportUsed: editing.localTransportUsed ?? null,
            dispatchedAt: editing.dispatchedAt, requiredDispatchDate: editing.requiredDispatchDate, handedOffAt: editing.handedOffAt,
            freight: editing.freight || (editing.handoffFreightTerms ? { payment: editing.handoffFreightTerms } : null),
          })}
          busy={save.isPending}
          onCancel={() => setEditing(null)}
          onConfirm={(lt) => save.mutate({ id: editing._id, lt })}
        />
      )}

      <PageHeader
        icon={Truck}
        eyebrow="Admin Panel • Dispatch"
        title="Transport Cost Module"
        description="Track and manage transport costs after dispatch: Handoff Freight Terms (Paid, To Pay, Self) and Local Transport (Porter, Suresh Solanki, Prashant Bhate, etc.)."
        actions={(
          <Button variant="outline" onClick={exportCsv} disabled={!view.length}>
            <Download className="h-4 w-4" /> Export CSV
          </Button>
        )}
      />

      {/* Filter toolbar */}
      <div className="flex flex-wrap items-center gap-2 rounded-card border border-border bg-card p-3 shadow-xs">
        <div className="flex rounded-lg border border-input p-0.5" role="group" aria-label="Date range">
          {PRESETS.map((p) => (
            <button
              key={p.key}
              type="button"
              onClick={() => { setPreset(p.key); setRange(p.range()); }}
              aria-pressed={preset === p.key}
              className={`rounded-md px-2.5 py-1.5 text-body-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring ${
                preset === p.key ? 'bg-primary text-on-primary' : 'text-on-surface-variant hover:bg-muted'
              }`}
            >
              {p.label}
            </button>
          ))}
        </div>
        <input
          type="date"
          aria-label="From date"
          value={from}
          onChange={(e) => { setPreset(''); setRange([e.target.value, to]); }}
          className="rounded-lg border border-input bg-surface px-2 py-1.5 text-body-sm text-on-surface focus-visible:border-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        />
        <span className="text-body-sm text-on-surface-subtle">to</span>
        <input
          type="date"
          aria-label="To date"
          value={to}
          onChange={(e) => { setPreset(''); setRange([from, e.target.value]); }}
          className="rounded-lg border border-input bg-surface px-2 py-1.5 text-body-sm text-on-surface focus-visible:border-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        />

        {/* Hand-off filters — what Sales chose for the third-party transporter. */}
        <div className="flex flex-wrap items-center gap-1.5 rounded-lg border border-indigo-200 bg-indigo-50/40 px-2 py-1" role="group" aria-label="Hand-off filters">
          <span className="text-label-sm font-semibold uppercase tracking-wide text-indigo-700">Hand-off</span>
          <select aria-label="Hand-off transporter" value={handoffWho} onChange={(e) => setHandoffWho(e.target.value)} className="rounded-lg border border-input bg-surface px-2.5 py-1.5 text-body-sm text-on-surface focus-visible:border-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">
            <option value="">All Transporters</option>
            {handoffTransporters.map((t) => <option key={t} value={t}>{t}</option>)}
          </select>
          <select aria-label="Hand-off terms" value={handoffTermFilter} onChange={(e) => setHandoffTermFilter(e.target.value)} className="rounded-lg border border-input bg-surface px-2.5 py-1.5 text-body-sm text-on-surface focus-visible:border-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">
            <option value="">All Hand-off Terms</option>
            {TERM_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
        </div>

        {/* Local transport filters — what Dispatch set for the leg to that transporter. */}
        <div className="flex flex-wrap items-center gap-1.5 rounded-lg border border-emerald-200 bg-emerald-50/40 px-2 py-1" role="group" aria-label="Local transport filters">
          <span className="text-label-sm font-semibold uppercase tracking-wide text-emerald-700">Local transport</span>
          <select aria-label="Local transporter" value={who} onChange={(e) => setWho(e.target.value)} className="rounded-lg border border-input bg-surface px-2.5 py-1.5 text-body-sm text-on-surface focus-visible:border-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">
            <option value="">All Local Transporters</option>
            {transporters.map((t) => <option key={t} value={t}>{t}</option>)}
          </select>
          <select aria-label="Local transport terms" value={localTermFilter} onChange={(e) => setLocalTermFilter(e.target.value)} className="rounded-lg border border-input bg-surface px-2.5 py-1.5 text-body-sm text-on-surface focus-visible:border-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">
            <option value="">All Local Terms</option>
            {TERM_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
        </div>

        <input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          aria-label="Search transport cost module"
          placeholder="Search order, client, transporter, vehicle, mobile, tracking…"
          className="min-w-[240px] flex-1 rounded-lg border border-input bg-surface px-3 py-1.5 text-body-sm text-on-surface placeholder:text-on-surface-subtle focus-visible:border-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        />
        {anyFilter && (
          <Button size="sm" variant="ghost" onClick={clearFilters}>Clear filters</Button>
        )}
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
        <div className="rounded-card border border-border bg-card p-4 shadow-xs">
          <p className="text-label-sm uppercase tracking-wider text-on-surface-subtle">Total Transport Cost in View</p>
          <p className="mt-1 font-mono text-data-lg text-xl font-semibold tabular-nums text-on-surface">{formatINR(total + freightTotal)}</p>
          <dl className="mt-2 space-y-1 text-body-sm text-on-surface-variant">
            <div className="flex items-baseline justify-between gap-3"><dt>Local Transport</dt><dd className="font-mono tabular-nums text-on-surface">{formatINR(total)}</dd></div>
            <div className="flex items-baseline justify-between gap-3"><dt>Third-Party Freight</dt><dd className="font-mono tabular-nums text-on-surface">{formatINR(freightTotal)}</dd></div>
            <div className="flex items-baseline justify-between gap-3"><dt>Dispatched Orders in View</dt><dd className="font-mono tabular-nums text-on-surface">{view.length}</dd></div>
          </dl>
          {amountsPending > 0 && (
            <p className="mt-2 text-body-sm font-medium text-warning-text">{amountsPending} amount{amountsPending === 1 ? '' : 's'} pending entry by Admin</p>
          )}
        </div>

        <div className="rounded-card border border-border bg-card p-4 shadow-xs">
          <p className="text-label-sm uppercase tracking-wider text-on-surface-subtle">By Local Transporter</p>
          {byTransporter.length === 0 ? <p className="mt-2 text-body-sm text-on-surface-subtle">No transport recorded in this period.</p> : (
            <ul className="mt-2 divide-y divide-border">
              {byTransporter.slice(0, 5).map(([k, v]) => (
                <li key={k} className="flex items-baseline justify-between gap-3 py-1 text-body-sm">
                  <span className="min-w-0 truncate text-on-surface font-medium">{k} <span className="font-mono tabular-nums text-on-surface-subtle">({v.trips})</span></span>
                  <span className="font-mono tabular-nums font-semibold text-on-surface">{formatINR(v.amount)}</span>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="rounded-card border border-border bg-card p-4 shadow-xs">
          <p className="text-label-sm uppercase tracking-wider text-on-surface-subtle">By Terms</p>
          {view.length === 0 ? <p className="mt-2 text-body-sm text-on-surface-subtle">No terms recorded in this period.</p> : (
            <div className="mt-2 grid grid-cols-2 gap-3">
              {([
                ['Hand-off (orders)', byHandoffTerm, 'text-indigo-700'],
                ['Local transport (cost)', byLocalTerm, 'text-emerald-700'],
              ] as const).map(([title, list, tone]) => (
                <div key={title}>
                  <p className={`text-[11px] font-semibold uppercase tracking-wide ${tone}`}>{title}</p>
                  <ul className="mt-1 divide-y divide-border">
                    {list.length === 0 ? <li className="py-1 text-body-sm text-on-surface-subtle">—</li> : list.map(([k, v]) => (
                      <li key={k} className="flex items-baseline justify-between gap-2 py-1 text-body-sm">
                        <span className="min-w-0 truncate font-medium text-on-surface">{k} <span className="font-mono tabular-nums text-on-surface-subtle">({v.count})</span></span>
                        <span className="font-mono tabular-nums font-semibold text-on-surface">{formatINR(v.amount)}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Main Transport Cost Table */}
      <div className="overflow-x-auto rounded-card border border-border bg-card shadow-xs">
        <table className="w-full min-w-[1280px] text-left text-body-md" aria-busy={isLoading}>
          <caption className="sr-only">Transport cost module: details updated in Handoff and Local Transport visible after dispatch</caption>
          <thead className="sticky top-0 z-10">
            <tr className="border-b border-border bg-muted text-label-sm font-semibold uppercase tracking-wider text-on-surface-variant">
              <th scope="col" className="px-3 py-2.5">Dispatched</th>
              <th scope="col" className="px-3 py-2.5">Order</th>
              <th scope="col" className="px-3 py-2.5">Client &amp; Destination</th>
              <th scope="col" className="px-3 py-2.5">Handoff &amp; Transporter</th>
              <th scope="col" className="px-3 py-2.5 text-right">Freight Cost</th>
              <th scope="col" className="px-3 py-2.5">Local Transporter</th>
              <th scope="col" className="px-3 py-2.5">Mobile &amp; Vehicle</th>
              <th scope="col" className="px-3 py-2.5 text-right">Local Cost</th>
              <th scope="col" className="px-3 py-2.5">Mode</th>
              <th scope="col" className="px-3 py-2.5">Tracking / LR</th>
              <th scope="col" className="px-3 py-2.5 text-right">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border text-on-surface-variant">
            {isLoading && Array.from({ length: 6 }).map((_, i) => (
              <tr key={`skeleton-${i}`}>
                {Array.from({ length: 11 }).map((__, j) => <td key={j} className="px-3 py-2.5"><Skeleton className="h-3.5 w-full" /></td>)}
              </tr>
            ))}
            {!isLoading && isError && (
              <tr>
                <td colSpan={11} className="px-3 py-8 text-center">
                  <p className="text-body-md font-medium text-destructive">{getApiErrorMessage(error, 'Could not load the transport cost register.')}</p>
                  <p className="mt-1 text-body-sm text-on-surface-variant">Retry, or widen the date range if you expected an older order.</p>
                </td>
              </tr>
            )}
            {!isLoading && !isError && view.length === 0 && (
              <tr>
                <td colSpan={11} className="px-3 py-8 text-center">
                  <p className="text-body-md font-medium text-on-surface">No dispatched orders with transport in this period</p>
                  <p className="mt-1 text-body-sm text-on-surface-variant">Clear the filters or pick a wider date range to see older dispatches.</p>
                </td>
              </tr>
            )}
            {!isLoading && !isError && view.map((r) => {
              const handoffTerm = handoffTermOf(r);
              const localName = s(r.localTransport?.name);
              const localAmount = s(r.localTransport?.amount);
              const freightAmount = s(r.freight?.amount);

              return (
                <tr key={r._id} className="hover:bg-muted/50 transition-colors">
                  {/* Dispatched Date */}
                  <td className="whitespace-nowrap px-3 py-2.5">
                    {r.dispatchedAt ? (
                      <span className="font-mono tabular-nums font-medium text-on-surface">
                        {new Date(r.dispatchedAt).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}
                      </span>
                    ) : (
                      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold bg-amber-50 text-amber-700 border border-amber-200">
                        Waiting dispatch
                      </span>
                    )}
                  </td>

                  {/* Order Number */}
                  <td className="px-3 py-2.5">
                    <Link to={`/dispatch/orders/${r._id}`} className="font-mono font-bold text-primary hover:text-primary-dim hover:underline">
                      {r.orderNumber}
                    </Link>
                  </td>

                  {/* Client & Destination */}
                  <td className="px-3 py-2.5">
                    <div className="font-semibold text-on-surface">{r.customerName || '—'}</div>
                    <div className="text-body-xs text-on-surface-subtle">{r.destination || ''}</div>
                  </td>

                  {/* Point 55: Handoff & Transporter */}
                  <td className="px-3 py-2.5">
                    <div className="flex flex-col gap-1 items-start">
                      <span className="font-medium text-on-surface">
                        {r.transporter || r.courierName || (r.transportMode === 'local_transport' ? 'Local Transport' : 'Direct')}
                      </span>
                      {handoffTerm ? (
                        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-bold border ${payBadgeClass(handoffTerm)}`}>
                          {payLabel(handoffTerm)}
                        </span>
                      ) : (
                        <span className="text-[11px] text-on-surface-subtle italic">Term not set</span>
                      )}
                    </div>
                  </td>

                  {/* Freight Cost */}
                  <td className="whitespace-nowrap px-3 py-2.5 text-right">
                    {!hasFreight(r) || freightAmount === '' ? (
                      <span className="text-on-surface-subtle" title="Optional">—</span>
                    ) : (
                      <div className="flex flex-col items-end">
                        <span className="font-mono tabular-nums font-bold text-on-surface">{formatINR(freightOf(r))}</span>
                        <span className="text-body-xs text-on-surface-subtle">{payLabel(r.freight?.payment)}</span>
                      </div>
                    )}
                  </td>

                  {/* Local Transporter */}
                  <td className="px-3 py-2.5">
                    {r.localTransportUsed === false ? (
                      <span className="text-on-surface-subtle text-xs italic">No local transport</span>
                    ) : localName ? (
                      <div className="flex flex-col gap-0.5">
                        <span className="font-semibold text-on-surface">{localName}</span>
                        <span className="text-[10px] text-indigo-600 font-medium">Local Carrier</span>
                      </div>
                    ) : (
                      <span className="text-danger-text text-xs font-medium">Missing</span>
                    )}
                  </td>

                  {/* Mobile & Vehicle */}
                  <td className="whitespace-nowrap px-3 py-2.5">
                    {r.localTransportUsed === false ? (
                      <span className="text-on-surface-subtle">—</span>
                    ) : (
                      <div className="space-y-0.5">
                        {s(r.localTransport?.vehicleNo) ? (
                          <div className="font-mono uppercase font-bold text-xs text-on-surface">
                            {s(r.localTransport?.vehicleNo)}
                          </div>
                        ) : null}
                        {s(r.localTransport?.mobile ?? r.localTransport?.driverPhone) ? (
                          <div className="font-mono text-xs text-on-surface-subtle">
                            📞 {s(r.localTransport?.mobile ?? r.localTransport?.driverPhone)}
                          </div>
                        ) : null}
                        {!s(r.localTransport?.vehicleNo) && !s(r.localTransport?.mobile) && (
                          <span className="text-on-surface-subtle">—</span>
                        )}
                      </div>
                    )}
                  </td>

                  {/* Local Cost */}
                  <td className="whitespace-nowrap px-3 py-2.5 text-right">
                    {r.localTransportUsed === false ? (
                      <span className="text-on-surface-subtle">—</span>
                    ) : localAmount === '' ? (
                      <span className="text-amber-600 text-xs font-medium">Pending entry</span>
                    ) : (
                      <div className="flex flex-col items-end">
                        <span className="font-mono tabular-nums font-bold text-on-surface">{formatINR(amountOf(r))}</span>
                        {s(r.localTransport?.payment) && (
                          <span className={`inline-flex items-center px-1.5 py-0.2 rounded text-[10px] font-semibold border ${payBadgeClass(r.localTransport?.payment)}`}>
                            {payLabel(r.localTransport?.payment)}
                          </span>
                        )}
                      </div>
                    )}
                  </td>

                  {/* Payment Mode */}
                  <td className="px-3 py-2.5">
                    <span className="text-xs">{s(r.localTransport?.paymentMode) || '—'}</span>
                  </td>

                  {/* Tracking / LR */}
                  <td className="px-3 py-2.5">
                    {s(r.localTransport?.trackingNo) ? (
                      <span className="font-mono text-xs font-semibold text-slate-800 bg-slate-100 px-2 py-0.5 rounded border border-slate-200">
                        {s(r.localTransport?.trackingNo)}
                      </span>
                    ) : (
                      <span className="text-on-surface-subtle text-xs">—</span>
                    )}
                  </td>

                  {/* Actions */}
                  <td className="px-3 py-2.5 text-right">
                    {canEdit && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setEditing(r)}
                        aria-label={`Edit transport cost for ${r.orderNumber}`}
                        className="gap-1.5 text-xs font-medium"
                      >
                        <Pencil className="h-3.5 w-3.5" /> Edit
                      </Button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>

          {!isLoading && !isError && view.length > 0 && (
            <tfoot>
              <tr className="border-t-2 border-border bg-muted/70 font-semibold">
                <td className="px-3 py-2.5 text-label-sm uppercase tracking-wider text-on-surface-variant" colSpan={4}>
                  Total Transport Cost in View
                </td>
                <td className="px-3 py-2.5 text-right font-mono tabular-nums font-bold text-on-surface">
                  {formatINR(freightTotal)}
                </td>
                <td className="px-3 py-2.5" colSpan={2} />
                <td className="px-3 py-2.5 text-right font-mono tabular-nums font-bold text-on-surface">
                  {formatINR(total)}
                </td>
                <td className="px-3 py-2.5" colSpan={3} />
              </tr>
            </tfoot>
          )}
        </table>
      </div>
    </div>
  );
}
